import React, { FC, useContext, useEffect, useRef, useState } from "react"
import { NoteTypeEnum, PdfPreferences } from "../../../client/backend-client/generated"
import { NoteContext } from "../../NotePage"
import { useUpdateNote } from "../../../queries/NoteQueries"
import { Button, Col, Flex, Modal, Row, Space, Spin, theme, Typography } from "antd"
import { AppMetierSwitch } from "../../../components/AppMetierSwitch"
import { useDictionary } from "../../../utils/dictionary/dictionary"
import { retrievePercentageText } from "../../financial/income-statement/IncomeStatementForm"
import Loader from "../../../components/Loader/Loader"
import { PeriodsContext } from "../../financial/PeriodsContext"
import { useGetAllControls } from "../../financial/operations/ControlsQueries"
import { LoadingOutlined } from "@ant-design/icons"

const { Text } = Typography

export const PdfDialogContent: FC<{
  isOpen: boolean
  cancelClick: () => void
  generateFct: (pdfOptions: PdfPreferences) => void
}> = ({ isOpen, cancelClick, generateFct }) => {
  const dictionary = useDictionary()
  const { useToken } = theme
  const { note, noteId, noteSettings } = useContext(NoteContext)
  const { forecastPeriods, previousPeriods } = useContext(PeriodsContext)
  const [pdfPreferences, setPdfPreferences] = useState<PdfPreferences>(note.pdfPreferences)
  const [savePrefs, setSavePrefs] = useState<boolean>(true)
  const [showSpinner, setShowSpinner] = useState<boolean>(false)

  const disableNoPeriodTooltip = forecastPeriods.length === 0 ? "Pas de périodes financières définies" : undefined
  const disableNoPreviousPeriodTooltip = previousPeriods.length === 0 ? "Pas d'exercices passés définis" : undefined

  const refs = useRef({
    isMounted: false
  })

  const { token } = useToken()

  const { isLoading: controlsInProgress, data: allControlsOk } = useGetAllControls(noteId)
  
  const updateNoteMutation = useUpdateNote(noteId)

  useEffect(() => {
    if (refs.current.isMounted) {
      if (savePrefs) {
        updateNoteMutation.mutate({
          noteId,
          pdfPreferences: pdfPreferences
        })
      }
    } else {
      refs.current.isMounted = true
    }
  }, [pdfPreferences, savePrefs])

  if (controlsInProgress)  {
    return <Spin indicator={<LoadingOutlined style={{ color: token.colorPrimary }} spin />} />
  }

  return (
    <Modal
      open={isOpen}
      width={750}
      closeIcon={false}
      destroyOnHidden
      afterClose={() => {
        setShowSpinner(false)
      }}
      footer={[
        <div key="footer-buttons" style={{ marginTop: "30px", textAlign: "center" }}>
          {!showSpinner && (
            <Space direction="horizontal">
              <Button
                type="primary"
                shape="round"
                size="large"
                style={{
                  backgroundColor: "var(--main-violet)",
                  fontWeight: "bold"
                }}
                onClick={() => {
                  generateFct(pdfPreferences)
                  setShowSpinner(true)
                }}
              >
                Générer la note
              </Button>
              <Button shape="round" size="large" onClick={cancelClick}>
                Annuler
              </Button>
            </Space>
          )}
          {showSpinner && (
            <div>
              Veuillez patienter pendant la génération de la note pdf
              <br />
              <Loader />
            </div>
          )}
        </div>
      ]}
      style={{
        minWidth: "35%"
      }}
    >
      <Flex vertical>
        <Text
          style={{
            color: "var(--main-green-1)",
            fontSize: "20px",
            marginBottom: "15px",
            lineHeight: "26px"
          }}
        >
          Contenu de la note
        </Text>
        <Text style={{ paddingBottom: "15px" }}>
          Certaines rubriques sont optionnelles, cochez celles que vous souhaitez intégrer à la note
          PDF :
        </Text>
        {allControlsOk === false && (
          <Text style={{ paddingBottom: "15px", color: token.colorError }}>
            Attention, les règles de partage et d'analyse du risque ne sont pas respectées
          </Text>
        )}
      </Flex>
      <Row style={{ padding: "0 15px 15px 15px" }}>
        <Col span={12}>
          <Flex vertical>
            <Text>Présentation</Text>
            <Text>Analyse du projet</Text>
            <AppMetierSwitch
              style={{ paddingLeft: "30px" }}
              checked={pdfPreferences.revelateurEngagement}
              onChange={() =>
                setPdfPreferences(pdfPreferences => ({
                  ...pdfPreferences,
                  revelateurEngagement: !pdfPreferences.revelateurEngagement
                }))
              }
              label={"Révélateur d'engagement"}
            />
          </Flex>
        </Col>
      </Row>
      <Row style={{ padding: "0 15px 15px 15px" }}>
        <Col span={12}>
          <Flex vertical>
            <Text strong>1. Évaluation du projet</Text>
            <Text>Environnement</Text>
            <Text>Positionnement</Text>
            <Text>Moyens</Text>
            <Text>Équipe</Text>
          </Flex>
        </Col>
        <Col span={12}>
          <Flex vertical>
            <Text strong>2. Analyse financière</Text>
            <Text>{dictionary.cr.ongletName}</Text>
            {note.type === NoteTypeEnum.REPRISE && pdfPreferences.showAnterieurPeriodsOption && (
              <AppMetierSwitch
                style={{ paddingLeft: "30px" }}
                checked={pdfPreferences.allAnterieursPeriods}
                onChange={() =>
                  setPdfPreferences(pdfPreferences => ({
                    ...pdfPreferences,
                    allAnterieursPeriods: !pdfPreferences.allAnterieursPeriods
                  }))
                }
                label={"Afficher tous les exercices du cédant"}
                disabled={disableNoPreviousPeriodTooltip !== undefined}
                toolTip={disableNoPreviousPeriodTooltip}
              />
            )}
            {pdfPreferences.showCaDetails && (
              <AppMetierSwitch
                style={{ paddingLeft: "30px" }}
                checked={pdfPreferences.caDetails}
                onChange={() =>
                  setPdfPreferences(pdfPreferences => ({
                    ...pdfPreferences,
                    caDetails: !pdfPreferences.caDetails
                  }))
                }
                label={"Détail du CA"}
                disabled={disableNoPeriodTooltip !== undefined}
                toolTip={disableNoPeriodTooltip}
              />
            )}
            {pdfPreferences.showSubventionDetails && (
              <AppMetierSwitch
                style={{ paddingLeft: "30px" }}
                checked={pdfPreferences.subventionDetails}
                onChange={() =>
                  setPdfPreferences(pdfPreferences => ({
                    ...pdfPreferences,
                    subventionDetails: !pdfPreferences.subventionDetails
                  }))
                }
                label={"Détail des subventions d'exploitation"}
                disabled={disableNoPeriodTooltip !== undefined}
                toolTip={disableNoPeriodTooltip}
              />
            )}
            <AppMetierSwitch
              style={{ paddingLeft: "30px" }}
              checked={pdfPreferences.sig}
              onChange={() =>
                setPdfPreferences(pdfPreferences => ({
                  ...pdfPreferences,
                  sig: !pdfPreferences.sig
                }))
              }
              label={"SIG"}
              disabled={disableNoPeriodTooltip !== undefined}
              toolTip={disableNoPeriodTooltip}
            />
            {pdfPreferences.showChargesDetails && (
              <AppMetierSwitch
                style={{ paddingLeft: "30px" }}
                checked={pdfPreferences.chargesDetails}
                onChange={() =>
                  setPdfPreferences(pdfPreferences => ({
                    ...pdfPreferences,
                    chargesDetails: !pdfPreferences.chargesDetails
                  }))
                }
                label={"Détail des charges"}
                disabled={disableNoPeriodTooltip !== undefined}
                toolTip={disableNoPeriodTooltip}
              />
            )}
            {noteSettings.referenceAmount !== "NONE" && (
              <AppMetierSwitch
                style={{ paddingLeft: "30px" }}
                checked={pdfPreferences.pourcentageCR}
                onChange={() =>
                  setPdfPreferences(pdfPreferences => ({
                    ...pdfPreferences,
                    pourcentageCR: !pdfPreferences.pourcentageCR
                  }))
                }
                label={retrievePercentageText(noteSettings.referenceAmount)}
                disabled={disableNoPeriodTooltip !== undefined}
                toolTip={disableNoPeriodTooltip}
              />
            )}
            <Text>Plan de financement</Text>
            <AppMetierSwitch
              style={{ paddingLeft: "30px" }}
              checked={pdfPreferences.indicateurs}
              onChange={() =>
                setPdfPreferences(pdfPreferences => ({
                  ...pdfPreferences,
                  indicateurs: !pdfPreferences.indicateurs
                }))
              }
              label={"Indicateurs financiers (ratios)"}
              disabled={disableNoPeriodTooltip !== undefined}
              toolTip={disableNoPeriodTooltip}
            />
            <AppMetierSwitch
              style={{ paddingLeft: "30px" }}
              checked={pdfPreferences.rappelBFR}
              onChange={() =>
                setPdfPreferences(pdfPreferences => ({
                  ...pdfPreferences,
                  rappelBFR: !pdfPreferences.rappelBFR
                }))
              }
              label={"Rappel BFR (ratios)"}
              disabled={disableNoPeriodTooltip !== undefined}
              toolTip={disableNoPeriodTooltip}
            />
            <AppMetierSwitch
              checked={pdfPreferences.benchmark}
              onChange={() =>
                setPdfPreferences(pdfPreferences => ({
                  ...pdfPreferences,
                  benchmark: !pdfPreferences.benchmark
                }))
              }
              label={"Benchmark"}
              disabled={disableNoPeriodTooltip !== undefined}
              toolTip={disableNoPeriodTooltip}
            />
            {pdfPreferences.showInvestissements && (
              <AppMetierSwitch
                checked={pdfPreferences.investissements}
                onChange={() =>
                  setPdfPreferences(pdfPreferences => ({
                    ...pdfPreferences,
                    investissements: !pdfPreferences.investissements
                  }))
                }
                label={"Investissements"}
                disabled={disableNoPeriodTooltip !== undefined}
                toolTip={disableNoPeriodTooltip}
              />
            )}
            {pdfPreferences.showBilanCedant &&
              (note.type === NoteTypeEnum.STRUCTURE_EXISTANTE ? (
                <Text>{dictionary.bilan.ongletName}</Text>
              ) : (
                <AppMetierSwitch
                  checked={pdfPreferences.bilanCedant}
                  onChange={() =>
                    setPdfPreferences(pdfPreferences => ({
                      ...pdfPreferences,
                      bilanCedant: !pdfPreferences.bilanCedant
                    }))
                  }
                  label={dictionary.bilan.ongletName}
                  disabled={disableNoPreviousPeriodTooltip !== undefined}
                  toolTip={disableNoPreviousPeriodTooltip}
                />
              ))}
          </Flex>
        </Col>
      </Row>
      <Flex justify={"center"} style={{ paddingTop: "10px" }}>
        <AppMetierSwitch
          style={{ fontWeight: "bold" }}
          checked={savePrefs}
          onChange={() => setSavePrefs(!savePrefs)}
          label={"Conserver ces choix pour le prochain export PDF de cette note."}
        />
      </Flex>
    </Modal>
  )
}
